import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react';
import { apiService, Show, Episode, Channel, Panelist, ShowSegment } from '../services/api';

interface AudioContextType {
  // Current session data
  currentSession: any | null;
  currentShow: Show | null;
  currentEpisode: Episode | null;
  currentChannel: Channel | null;
  currentSegments: ShowSegment[];
  currentPanelists: Panelist[];
  
  // Playback state
  isPlaying: boolean;
  isMuted: boolean;
  currentSegmentIndex: number;
  currentPartIndex: number;
  currentSpeaker: string | null;
  
  // Audio controls
  play: () => void;
  pause: () => void;
  stop: () => void;
  toggleMute: () => void;
  nextSegment: () => void;
  previousSegment: () => void;
  setCurrentSegment: (index: number) => void;
  
  // Session management
  startSession: (episodeId: string, episodeTitle: string) => Promise<void>;
  clearSession: () => void;
  
  // Audio data
  audioBlobUrls: Map<string, string>;
  totalDuration: number;
}

const AudioContext = createContext<AudioContextType | undefined>(undefined);

export const useAudio = () => {
  const context = useContext(AudioContext);
  if (context === undefined) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
};

interface AudioProviderProps {
  children: React.ReactNode;
}

export const AudioProvider: React.FC<AudioProviderProps> = ({ children }) => {
  // Audio element ref
  const audioRef = useRef<HTMLAudioElement>(null);
  const currentAudioSrcRef = useRef<string>('');
  const preFetchQueueRef = useRef<Set<string>>(new Set());
  const audioBlobUrlsRef = useRef<Map<string, string>>(new Map());

  // Session state
  const [currentSession, setCurrentSession] = useState<any | null>(null);
  const [currentShow, setCurrentShow] = useState<Show | null>(null);
  const [currentEpisode, setCurrentEpisode] = useState<Episode | null>(null);
  const [currentChannel, setCurrentChannel] = useState<Channel | null>(null);
  const [currentSegments, setCurrentSegments] = useState<ShowSegment[]>([]);
  const [currentPanelists, setCurrentPanelists] = useState<Panelist[]>([]);

  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState(0);
  const [currentPartIndex, setCurrentPartIndex] = useState(0);
  const [currentSpeaker, setCurrentSpeaker] = useState<string | null>(null);
  const [autoPlayAttempted, setAutoPlayAttempted] = useState(false);
  const [audioBlobUrls, setAudioBlobUrls] = useState<Map<string, string>>(new Map());

  // Calculate total duration
  const totalDuration = currentSegments?.reduce((total, item) => {
    if (item.scriptParts && item.scriptParts.length > 0) {
      return total + (item.audioDuration || 0);
    }
    return total + (item.audioDuration || 0);
  }, 0) || 0;

  // Function to get audio blob URL (cached)
  const getAudioBlobUrl = useCallback(async (segment: ShowSegment, partIndex?: number): Promise<string> => {
    const key = partIndex !== undefined ? `${segment._id}_${partIndex}` : `${segment._id}_segment`;
    
    // Check if we already have the blob URL cached
    if (audioBlobUrlsRef.current.has(key)) {
      return audioBlobUrlsRef.current.get(key)!;
    }

    try {
      let blob: Blob;
      
      if (segment.scriptParts && segment.scriptParts.length > 0 && partIndex !== undefined) {
        blob = await apiService.getAudioFile(segment._id, partIndex);
      } else {
        blob = await apiService.getSegmentAudio(segment._id);
      }
      
      const blobUrl = URL.createObjectURL(blob);
      
      // Cache the blob URL
      audioBlobUrlsRef.current.set(key, blobUrl);
      setAudioBlobUrls(prev => new Map(prev).set(key, blobUrl));
      
      return blobUrl;
    } catch (error) {
      console.error('Error getting audio blob URL:', error);
      throw error;
    }
  }, []);

  // Function to pre-fetch next audio parts
  const preFetchNextAudioParts = useCallback(async (currentSegment: ShowSegment, currentPart: number) => {
    if (!currentSegment?.scriptParts || currentSegment.scriptParts.length === 0) return;

    const preFetchCount = 3;
    const tasks: Promise<void>[] = [];

    for (let i = 1; i <= preFetchCount; i++) {
      const nextPartIndex = currentPart + i;
      
      if (nextPartIndex < currentSegment.scriptParts.length) {
        const key = `${currentSegment._id}_${nextPartIndex}`;
        
        if (audioBlobUrlsRef.current.has(key) || preFetchQueueRef.current.has(key)) {
          continue;
        }

        preFetchQueueRef.current.add(key);

        const task = (async () => {
          try {
            await getAudioBlobUrl(currentSegment, nextPartIndex);
          } catch (error) {
            console.error(`Pre-fetch failed for part ${nextPartIndex}:`, error);
          } finally {
            preFetchQueueRef.current.delete(key);
          }
        })();

        tasks.push(task);
      }
    }

    // Pre-fetch first part of next segment
    if (currentSegmentIndex < currentSegments.length - 1) {
      const nextSegment = currentSegments[currentSegmentIndex + 1];
      if (nextSegment?.scriptParts && nextSegment.scriptParts.length > 0) {
        const key = `${nextSegment._id}_0`;
        
        if (!audioBlobUrlsRef.current.has(key) && !preFetchQueueRef.current.has(key)) {
          preFetchQueueRef.current.add(key);

          const task = (async () => {
            try {
              await getAudioBlobUrl(nextSegment, 0);
            } catch (error) {
              console.error(`Pre-fetch failed for next segment first part:`, error);
            } finally {
              preFetchQueueRef.current.delete(key);
            }
          })();

          tasks.push(task);
        }
      }
    }

    if (tasks.length > 0) {
      Promise.allSettled(tasks);
    }
  }, [currentSegmentIndex, currentSegments, getAudioBlobUrl]);

  // Keep ref in sync with state
  useEffect(() => {
    audioBlobUrlsRef.current = audioBlobUrls;
  }, [audioBlobUrls]);

  // Set audio source when segment or part changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentSegments.length || currentSegmentIndex >= currentSegments.length) return;

    const currentSegment = currentSegments[currentSegmentIndex];
    if (!currentSegment) return;

    const setAudioSource = async () => {
      try {
        const blobUrl = await getAudioBlobUrl(currentSegment, currentPartIndex);
        
        if (currentAudioSrcRef.current !== blobUrl) {
          audio.src = blobUrl;
          currentAudioSrcRef.current = blobUrl;
          audio.load();
          
          // Auto-play after setting audio source
          if (!autoPlayAttempted) {
            try {
              await audio.play();
              setIsPlaying(true);
              setAutoPlayAttempted(true);
            } catch (e) {
              console.log('Auto-play prevented by browser:', e);
            }
          }
        } else if (!autoPlayAttempted) {
          try {
            await audio.play();
            setIsPlaying(true);
            setAutoPlayAttempted(true);
          } catch (e) {
            console.log('Auto-play prevented by browser:', e);
          }
        }
      } catch (error) {
        console.error('Error setting audio source:', error);
      }
    };

    setAudioSource();
  }, [currentSegments, currentSegmentIndex, currentPartIndex, getAudioBlobUrl, autoPlayAttempted]);

  // Trigger pre-fetching when segment or part changes
  useEffect(() => {
    if (currentSegments.length > 0 && currentSegments[currentSegmentIndex]) {
      preFetchNextAudioParts(currentSegments[currentSegmentIndex], currentPartIndex);
    }
  }, [currentSegments, currentSegmentIndex, currentPartIndex, preFetchNextAudioParts]);

  // Set current speaker when segment or part changes
  useEffect(() => {
    if (currentSegments.length > 0 && currentSegments[currentSegmentIndex]) {
      const currentSegment = currentSegments[currentSegmentIndex];
      if (currentSegment.scriptParts && currentSegment.scriptParts.length > 0) {
        const currentPart = currentSegment.scriptParts[currentPartIndex];
        if (currentPart) {
          setCurrentSpeaker(currentPart.panelistId);
        }
      }
    }
  }, [currentSegments, currentSegmentIndex, currentPartIndex]);

  // Handle audio ended event
  const handleAudioEnded = useCallback(() => {
    setIsPlaying(false);
    
    if (currentSegments.length === 0) return;
    
    const currentSegment = currentSegments[currentSegmentIndex];
    if (!currentSegment) return;
    
    if (currentSegment.scriptParts && currentSegment.scriptParts.length > 1) {
      // Multi-part segment
      if (currentPartIndex < currentSegment.scriptParts.length - 1) {
        // Move to next part
        setCurrentPartIndex(prev => prev + 1);
        setAutoPlayAttempted(false);
      } else {
        // Move to next segment
        if (currentSegmentIndex < currentSegments.length - 1) {
          setCurrentSegmentIndex(prev => prev + 1);
          setCurrentPartIndex(0);
          setAutoPlayAttempted(false);
        }
      }
    } else {
      // Single-part segment, move to next segment
      if (currentSegmentIndex < currentSegments.length - 1) {
        setCurrentSegmentIndex(prev => prev + 1);
        setCurrentPartIndex(0);
        setAutoPlayAttempted(false);
      }
    }
  }, [currentSegments, currentSegmentIndex, currentPartIndex]);

  // Audio control functions
  const play = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.play();
      setIsPlaying(true);
    }
  }, []);

  const pause = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  }, []);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  }, []);

  const toggleMute = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  }, [isMuted]);

  const nextSegment = useCallback(() => {
    if (currentSegmentIndex < currentSegments.length - 1) {
      setCurrentSegmentIndex(prev => prev + 1);
      setCurrentPartIndex(0);
      setAutoPlayAttempted(false);
    }
  }, [currentSegmentIndex, currentSegments.length]);

  const previousSegment = useCallback(() => {
    if (currentSegmentIndex > 0) {
      setCurrentSegmentIndex(prev => prev - 1);
      setCurrentPartIndex(0);
      setAutoPlayAttempted(false);
    }
  }, [currentSegmentIndex]);

  const setCurrentSegment = useCallback((index: number) => {
    if (index >= 0 && index < currentSegments.length) {
      setCurrentSegmentIndex(index);
      setCurrentPartIndex(0);
      setAutoPlayAttempted(false);
    }
  }, [currentSegments.length]);

  // Session management
  const startSession = useCallback(async (episodeId: string, episodeTitle: string) => {
    try {
      console.log('Starting session for episode:', episodeId);
      
      // Get episode data
      const episodeData = await apiService.getEpisode(episodeId);
      const showData = await apiService.getShow(episodeData.showId);
      const channelData = await apiService.getChannel(episodeData.channelId);
      const segmentsData = await apiService.getShowSegments(episodeData._id);
      const panelistsData = await apiService.getPanelists();
      
      // Filter panelists based on show structure
      const filteredPanelists = showData && 'panelistIds' in showData 
        ? panelistsData.filter(p => (showData as any).panelistIds.includes(p._id))
        : panelistsData;
      
      // Create session object
      const sessionName = `${episodeData.title} - ${new Date().toLocaleDateString()}`;
      const session = {
        _id: `session_${episodeId}_${Date.now()}`,
        sessionName,
        episodeId: episodeData._id,
        showId: showData._id,
        status: 'active',
        startedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Update state
      setCurrentSession(session);
      setCurrentShow(showData);
      setCurrentEpisode(episodeData);
      setCurrentChannel(channelData);
      setCurrentSegments(segmentsData);
      setCurrentPanelists(filteredPanelists);
      setCurrentSegmentIndex(0);
      setCurrentPartIndex(0);
      setAutoPlayAttempted(false);
      
      console.log('Session started successfully');
    } catch (error) {
      console.error('Error starting session:', error);
      throw error;
    }
  }, []);

  const clearSession = useCallback(() => {
    // Stop audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    
    // Clear all state
    setCurrentSession(null);
    setCurrentShow(null);
    setCurrentEpisode(null);
    setCurrentChannel(null);
    setCurrentSegments([]);
    setCurrentPanelists([]);
    setIsPlaying(false);
    setCurrentSegmentIndex(0);
    setCurrentPartIndex(0);
    setCurrentSpeaker(null);
    setAutoPlayAttempted(false);
    
    // Clear audio blob URLs
    audioBlobUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
    audioBlobUrlsRef.current.clear();
    setAudioBlobUrls(new Map());
  }, []);

  const value: AudioContextType = {
    // Current session data
    currentSession,
    currentShow,
    currentEpisode,
    currentChannel,
    currentSegments,
    currentPanelists,
    
    // Playback state
    isPlaying,
    isMuted,
    currentSegmentIndex,
    currentPartIndex,
    currentSpeaker,
    
    // Audio controls
    play,
    pause,
    stop,
    toggleMute,
    nextSegment,
    previousSegment,
    setCurrentSegment,
    
    // Session management
    startSession,
    clearSession,
    
    // Audio data
    audioBlobUrls,
    totalDuration,
  };

  return (
    <AudioContext.Provider value={value}>
      {children}
      {/* Hidden Audio Element */}
      <audio
        ref={audioRef}
        onEnded={handleAudioEnded}
        onLoadedData={() => {
          console.log('Audio loaded successfully');
        }}
        onError={(e) => {
          console.error('Audio error:', e);
        }}
      />
    </AudioContext.Provider>
  );
};
